/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片卡片上的标签悬浮层
 *   - 左上角:显示当前照片已打的标签 chip (多个时显示第 1 个 + "+N")
 *   - 右下角 hover 显示「+」按钮,点击弹 TagPickerPopover 选标签
 *   - 选中标签 → 调 batchBindLabels (filter.include = [photo.file]) 给单照片追加打标
 *   - chip 上的「×」可单独移除某个已打标签 (后端无 unbind 接口,本期不支持移除)
 *
 * 使用方式: 在 PhotoCard 组件内嵌套,叠在缩略图之上。
 *   父组件提供 photoFile + photoLabels + allTags + groupId,
 *   bind 成功通过 onLabelsChange 回调更新本地 state。
 */
import React, { useCallback, useState } from 'react'
import { message, Popover } from 'antd'
import { PlusOutlined, TagOutlined } from '@ant-design/icons'
import { batchBindLabels, batchUnbindLabels } from '../../photoTag/api'
import { trackLabelBindSuccess } from '../PhotoFilter/utils/sensorUtils'
import {
  trackPicspageClick,
  trackPicspageDialogShow,
  trackPicspageDialogClick,
  trackPicspageToast,
  trackPicspageResult
} from '../../photoTag/utils/track'
import type { PhotoTag } from '../../photoTag/types'
import TagPickerPopover from '../TagPickerPopover'
import { useBatchTagMode } from '../BatchTagMode'
import './index.scss'

export interface PhotoCardTag {
  id: string
  name: string
  color?: string
}

export interface PhotoCardTagOverlayProps {
  /** 单照片唯一标识 (传给后端 filter.include 用),一般是 photo.file (OSS 文件名) */
  photoFile: string
  /** 该照片当前已打的标签列表;父组件维护,bind 成功后通过 onLabelsChange 同步 */
  photoLabels: PhotoCardTag[]
  /** 当前团队所有可选标签 (调用方应已过滤 disabled);由父组件统一拉一次再传入,避免每张卡片单独拉 */
  allTags: PhotoTag[]
  /** 团队 ID,bind 接口必传 */
  groupId: string
  /** 是否禁用 + 按钮 (如非管理员/无团队标签时) */
  disabled?: boolean
  /** bind 成功 / 移除时的回调,父组件用于更新本地 labels state */
  onLabelsChange?: (labels: PhotoCardTag[]) => void
  /** 埋点 fromPlace,父组件按所在页传 */
  fromPlace?: string
  /**
   * 是否在缩略图上叠加显示 chip(左上).
   * - true (默认): 完整体验,缩略图左上有 chip + 右下有 + 按钮
   * - false: 仅显示 + 按钮,chip 由父组件在卡片其它位置渲染(如底部 info 区).
   *   主要用于现有 Photo 卡片底部已有标签 chip 渲染,避免重复。
   */
  showOverlayChip?: boolean
  /** 触发"批量"按钮时,把当前 photo 的 seq 传给 Provider,默认会被自动勾选 */
  photoSeq?: number
}

const PhotoCardTagOverlay: React.FC<PhotoCardTagOverlayProps> = ({
  photoFile,
  photoLabels,
  allTags,
  groupId,
  disabled,
  onLabelsChange,
  fromPlace = '全部照片卡片悬浮打标',
  showOverlayChip = true,
  photoSeq
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const batchMode = useBatchTagMode()

  const currentTagIds = photoLabels.map((l) => l.id)

  const fromPageForTrack = batchMode.fromPage

  const handleSelect = useCallback(
    async (tag: PhotoTag) => {
      if (!photoFile || !groupId) return
      // 埋点: dialog_click select_tag (popover 内选标签)
      trackPicspageDialogClick({
        popTitle: 'card_tag_popover',
        clickItem: 'select_tag',
        fromPage: fromPageForTrack,
        string1: String(tag.id),
        labelName: tag.name,
        tabName: 'team_label'
      })
      // 已打过的标签 (= 当前唯一标签) 再次点击 → 直接关闭, 不做任何操作
      if (currentTagIds.includes(tag.id)) {
        setPopoverVisible(false)
        return
      }
      setSubmitting(true)
      const t0 = Date.now()
      try {
        // 双保险: 后端 exclusive 还没部署时 unbind 旧的避免叠加成 2 个 (跟 Photo.handleChipReplaceCommit 同思路)
        const oldIds = photoLabels.map((l) => String(l.id))
        if (oldIds.length > 0) {
          await batchUnbindLabels({
            groupID: groupId,
            filter: { include: [photoFile], exclude: [] },
            labelIDs: oldIds
          })
        }
        // 「一张照片只能 1 个标签」约束: 后端 exclusive=true 单次原子清空+打新
        await batchBindLabels({
          groupID: groupId,
          filter: { include: [photoFile], exclude: [] },
          labelIDs: [tag.id],
          exclusive: true
        })
        // 老埋点保留, 跟 v3 新事件并行上报
        try {
          trackLabelBindSuccess({
            photoNum: 1,
            labelName: tag.name,
            fromPlace: '照片预览页打标签'
          })
        } catch (e) {
          // 埋点失败不阻塞打标主流程
        }
        // v3 新埋点: result + toast
        trackPicspageResult({
          actionName: 'bind_label_single_exclusive',
          actionResult: 'success',
          costTime: Date.now() - t0,
          photoNum: 1,
          number1: 1,
          list1: [tag.name],
          tabName: 'team_label',
          bool1: true,
          fromPage: fromPageForTrack
        })
        trackPicspageToast({
          toastName: 'single_bind_success',
          fromPage: fromPageForTrack
        })
        // localLabels 直接替换为 [new tag], 不 append (1 张照片 1 个标签)
        const next: PhotoCardTag[] = [
          { id: tag.id, name: tag.name, color: tag.color || '#2979FF' }
        ]
        onLabelsChange && onLabelsChange(next)
        setPopoverVisible(false)
      } catch (err) {
        const msg = (err as Error)?.message || '打标失败,请稍后再试'
        message.error(msg)
        trackPicspageResult({
          actionName: 'bind_label_single_exclusive',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: 1,
          number1: 1,
          list1: [tag.name],
          tabName: 'team_label',
          bool1: true,
          fromPage: fromPageForTrack
        })
        trackPicspageToast({
          toastName: 'single_bind_fail',
          fromPage: fromPageForTrack
        })
      } finally {
        setSubmitting(false)
      }
    },
    [
      photoFile,
      groupId,
      currentTagIds,
      photoLabels,
      onLabelsChange,
      fromPageForTrack
    ]
  )

  // 卡片左上 overlay chip 上的 × 移除 (showOverlayChip=true 才用, 当前未启用)
  const handleRemoveAttempt = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      const target = photoLabels[0]
      if (!target || !photoFile || !groupId || submitting) return
      // 埋点: click card_chip_remove
      trackPicspageClick({
        clickItem: 'card_chip_remove',
        fromPage: fromPageForTrack,
        string1: String(target.id),
        labelName: target.name,
        tabName: 'team_label'
      })
      setSubmitting(true)
      const t0 = Date.now()
      try {
        await batchUnbindLabels({
          groupID: groupId,
          filter: { include: [photoFile], exclude: [] },
          labelIDs: [String(target.id)]
        })
        trackPicspageResult({
          actionName: 'unbind_label_single',
          actionResult: 'success',
          costTime: Date.now() - t0,
          photoNum: 1,
          number1: 1,
          list1: [target.name],
          tabName: 'team_label',
          fromPage: fromPageForTrack
        })
        trackPicspageToast({
          toastName: 'single_unbind_success',
          fromPage: fromPageForTrack
        })
        const next = photoLabels.filter(
          (l) => String(l.id) !== String(target.id)
        )
        onLabelsChange && onLabelsChange(next)
      } catch (err) {
        const msg = (err as Error)?.message || '移除失败,请稍后再试'
        message.error(msg)
        trackPicspageResult({
          actionName: 'unbind_label_single',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: 1,
          number1: 1,
          list1: [target.name],
          tabName: 'team_label',
          fromPage: fromPageForTrack
        })
        trackPicspageToast({
          toastName: 'single_unbind_fail',
          fromPage: fromPageForTrack
        })
      } finally {
        setSubmitting(false)
      }
    },
    [
      photoLabels,
      photoFile,
      groupId,
      submitting,
      onLabelsChange,
      fromPageForTrack
    ]
  )

  const first = photoLabels[0]
  const extraCount = photoLabels.length - 1

  // 批量模式下不渲染任何 overlay (+ 按钮 / chip 都隐藏),
  // 卡片整体交给 batchMode 的勾选交互。
  // 注意: 此 early return 必须放在所有 hooks (useState/useBatchTagMode/useCallback) 之后,
  // 否则违反 React Hooks rules-of-hooks
  if (batchMode.isBatchMode) {
    return null
  }

  return (
    <>
      {/* 左上 chip;只显示第 1 个,有更多用 +N 提示。仅 showOverlayChip=true 时渲染 */}
      {showOverlayChip && first && (
        <span
          className="pcto-chip"
          style={{
            background: 'rgba(255,255,255,0.97)',
            border: `1px solid ${first.color || '#2979FF'}`,
            color: first.color || '#2979FF'
          }}
          onClick={(e) => e.stopPropagation()}
          title={photoLabels.map((l) => l.name).join('、')}
        >
          <TagOutlined className="pcto-chip__icon" />
          <span className="pcto-chip__name">{first.name}</span>
          {extraCount > 0 && (
            <span className="pcto-chip__more">+{extraCount}</span>
          )}
          <span className="pcto-chip__x" onClick={handleRemoveAttempt}>
            ×
          </span>
        </span>
      )}

      {/* 右下 + 按钮;hover 卡片时出现。
          外层 div 拦截 click 冒泡 — antd Popover 会克隆 child 注入自己的 onClick 打开浮层,
          直接在 button 上 stopPropagation 时机不对,无法阻止 click 继续冒泡到 .photo 容器
          (.photo 上挂了 onClick=onPreview, 不拦的话点 + 就同时进了大图)。
          外层 div onClick stopPropagation 是 bubble 阶段最稳妥的拦截方式。
          placement 用 topLeft, antd 在卡片右下边缘时会自动 adjust, 顶左对齐 + 按钮反而更稳。 */}
      <div
        className="pcto-add-host"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Popover
          trigger="click"
          placement="topLeft"
          align={{ offset: [0, -4] }}
          open={popoverVisible}
          onOpenChange={(v) => {
            if (submitting) return
            setPopoverVisible(v)
            if (v) {
              // popover 从关→开 = card + 按钮被点 → 上报 click + dialog_show
              trackPicspageClick({
                clickItem: 'card_add_btn',
                fromPage: fromPageForTrack
              })
              trackPicspageDialogShow({
                popTitle: 'card_tag_popover',
                fromPage: fromPageForTrack
              })
            } else {
              // 点蒙层 / Esc 关闭 (非选标签也非批量按钮)
              trackPicspageDialogClick({
                popTitle: 'card_tag_popover',
                clickItem: 'close',
                fromPage: fromPageForTrack
              })
            }
          }}
          classNames={{ root: 'pcto-popover-overlay' }}
          content={
            <TagPickerPopover
              tags={allTags}
              currentTagIds={currentTagIds}
              onSelect={handleSelect}
              onRequestBatch={() => {
                // 只上报一次 enter_batch_mode (click), 不再用 dialog_click open_manage 重复
                // — 之前是 hack 复用枚举, 但语义跟规范里"打开标签管理半屏"冲突.
                trackPicspageClick({
                  clickItem: 'enter_batch_mode',
                  fromPage: fromPageForTrack
                })
                setPopoverVisible(false)
                batchMode.enter(photoSeq)
              }}
              onSearch={() =>
                trackPicspageDialogClick({
                  popTitle: 'card_tag_popover',
                  clickItem: 'search',
                  fromPage: fromPageForTrack
                })
              }
              emptyHint="当前团队暂无可用标签,请联系管理员"
            />
          }
        >
          <button
            type="button"
            className={
              'pcto-add-btn' + (popoverVisible ? ' pcto-add-btn--active' : '')
            }
            disabled={disabled}
            aria-label="添加标签"
          >
            <PlusOutlined />
          </button>
        </Popover>
      </div>
    </>
  )
}

export default PhotoCardTagOverlay
